<template>
    <a v-if="!to" v-bind="$attrs">
        <slot />
    </a>
    <router-link v-else :to="to" custom v-slot="{ isActive, href, navigate }">
        <a
            v-bind="$attrs"
            :href="href"
            @contextmenu="onContextmenu"
            @click.stop="(ev) => nav(ev, navigate)"
            :class="isActive ? activeClass : null"
        >
            <slot />
        </a>
    </router-link>
</template>

<script lang="ts" setup>
import { url } from "@/config";
import { copyToClipboard } from "@/scripts/browser-util";
import { useFluent } from "fluent-vue";
import { NavigationFailure } from "vue-router";
import { mainRouter } from "@/router";
import {
    ContextMenuDynamic,
    magContextMenu,
} from "@/components/context-menu/mag-context-menu";
import { MenuItem } from "@/components/context-menu/mag-dynamic-menu";
import { defineAsyncComponent } from "vue";
import { openModal, openModalPage } from "@/composables/modal";
import { useTileStore } from "@/store/tile-store";

const { $t } = useFluent();
const tileStore = useTileStore();

defineOptions({
    inheritAttrs: false,
});

const props = withDefaults(
    defineProps<{
        to?: string;
        activeClass?: null | string;
        behavior?: null | "window" | "browser" | "modalWindow";
        contextBehavior?:
            | null
            | {
                  type: "native";
              }
            | {
                  type: "userCard";
                  username: string;
                  host?: string | null;
              };
    }>(),
    {
        activeClass: null,
        behavior: null,
    },
);

function onContextmenu(ev: MouseEvent) {
    const selection = window.getSelection();
    if (selection && selection.toString() !== "") return;

    if (props.contextBehavior) {
        switch (props.contextBehavior.type) {
            case "userCard":
                openModal(
                    {
                        dismissable: true,
                        lowBackdrop: true,
                        transformOrigin: {
                            x: ev.x,
                            y: ev.y,
                        },
                        anchor: {
                            kind: "around-point",
                            x: ev.x,
                            y: ev.y,
                            align: "start",
                            position: "bottom",
                        },
                    },
                    defineAsyncComponent(
                        () => import("@/components/MagUserPreview.vue"),
                    ),
                    ({ onClose }) => ({
                        onClosed: onClose,
                        userTag: {
                            username: props.contextBehavior.username,
                            host: props.contextBehavior.host ?? undefined,
                        },
                    }),
                );

                ev.preventDefault();
                ev.stopPropagation();
                return;
            case "native":
                return;
        }
    }

    magContextMenu(
        {
            items: [
                {
                    type: "label",
                    props: {},
                    text: props.to,
                },
                {
                    type: "button",
                    props: {
                        icon: "browser",
                    },
                    text: $t("openInWindow"),
                    action: () => {
                        tileStore.spawnPage(props.to);
                    },
                },
                {
                    type: "button",
                    props: {
                        icon: "arrows-out-simple",
                    },
                    text: $t("showInPage"),
                    action: () => {
                        mainRouter.push({
                            path: props.to,
                            query: {
                                inPage: true.toString(),
                            },
                        });
                    },
                },
                { type: "separator" },
                {
                    type: "a",
                    props: {
                        href: props.to,
                        target: "_blank",
                        icon: "arrow-square-out",
                    },
                    text: $t("openInNewTab"),
                },
                {
                    type: "button",
                    props: {
                        icon: "link-simple",
                    },
                    text: $t("copyLink"),
                    action: () => {
                        copyToClipboard(`${url}${props.to}`);
                    },
                },
            ] satisfies MenuItem[],
        },
        ContextMenuDynamic,
        {
            fromContextMenu: ev,
        },
    );
}

function nav(
    ev: MouseEvent,
    navigate: (e: MouseEvent) => Promise<NavigationFailure | void>,
) {
    if (!props.to) {
        return;
    }

    if (ev.ctrlKey || props.behavior === "browser") {
        return;
    }

    if (props.behavior === "window") {
        ev.preventDefault();
        return tileStore.spawnPage(props.to);
    } else if (props.behavior === "modalWindow") {
        ev.preventDefault();
        return openModalPage(
            {
                dismissable: true,
            },
            props.to,
        );
    }

    if (ev.shiftKey) {
        ev.preventDefault();
        return tileStore.spawnPage(props.to);
    }

    navigate(ev);
}
</script>
